import React, { useEffect, useRef } from 'react';
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Legend, Tooltip } from 'chart.js';

// Register Chart.js components
Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Legend, Tooltip);

export function LineReportChart({ data, datasetsConfig, xField = 'date', reverse = true }) {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    const extractValue = (element) => {
        if (!element || !element.props || !element.props?.children) return 0;
        const valueStr = element.props?.children;
        if (typeof valueStr === 'string') {
            const numStr = valueStr.replace(/[^0-9.-]/g, '');
            return parseFloat(numStr) || 0;
        }
        return 0;
    };

    useEffect(() => {
        if (data && data.length > 0 && chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const formattedData = reverse ? [...data].reverse() : data;

            const labels = formattedData.map(item => item[xField]);

            const datasets = datasetsConfig.map(cfg => ({
                label: cfg.label,
                data: formattedData.map(item => extractValue(item[cfg.field])),
                borderColor: cfg.color,
                backgroundColor: 'transparent',
                borderWidth: cfg.borderWidth || 3,
                tension: 0.4,
                fill: false,
                borderDash: cfg.dashed ? [5, 5] : [],
                pointBackgroundColor: cfg.color,
                pointBorderColor: '#fff',
                pointHoverRadius: 8,
                pointHoverBorderWidth: 2,
                pointRadius: 0,
                pointHitRadius: 10
            }));

            const ctx = chartRef.current.getContext('2d');

            chartInstance.current = new Chart(ctx, {
                type: 'line',
                data: {
                    labels,
                    datasets
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'nearest',
                        intersect: false
                    },
                    plugins: {
                        legend: {
                            position: 'top',
                            labels: {
                                usePointStyle: true,
                                padding: 20
                            }
                        },
                        tooltip: {
                            callbacks: {
                                label: function (context) {
                                    return `${context.dataset.label}: ₹${context.parsed.y.toLocaleString('en-IN')}`;
                                },
                                labelColor: function (context) {
                                    return {
                                        borderColor: context.dataset.borderColor,
                                        backgroundColor: context.dataset.borderColor,
                                        borderWidth: 2
                                    };
                                }
                            }
                        }
                    },
                    scales: {
                        y: {
                            beginAtZero: false,
                            title: {
                                display: true,
                                text: 'Amount (₹)'
                            },
                            ticks: {
                                callback: function (value) {
                                    return '₹' + value.toLocaleString('en-IN');
                                }
                            }
                        },
                        x: {
                            title: {
                                display: true,
                                text: 'Date'
                            },
                            grid: {
                                display: false
                            }
                        }
                    }
                }
            });
        }

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [data, datasetsConfig, xField, reverse]);

    return (
        <div style={{ height: '400px', width: '100%', padding: '10px', position: 'relative' }}>
            <canvas ref={chartRef} />
        </div>
    );
}
